import React from "react";
import { useTranslation } from "react-i18next";
import { Box, IconButton, Stack, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import Modal from "components/basic/modal";
import Button from "components/basic/button";
import Typography from "components/basic/typography";

import { CustomEntry } from "./types";
import { formatDateShort } from "./helpers";

interface DeleteCustomDateDialogProps {
  entry: CustomEntry;
  onCancel: () => void;
  onConfirm: () => void;
}

const DeleteCustomDateDialog: React.FC<DeleteCustomDateDialogProps> = ({
  entry,
  onCancel,
  onConfirm,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("calendar");

  const label =
    entry.mode === "single"
      ? formatDateShort(entry.date)
      : `${formatDateShort(entry.startDate)} – ${formatDateShort(
          entry.endDate,
        )}`;
  const noteSuffix = entry.note ? ` (${entry.note})` : "";

  return (
    <Modal onClose={onCancel}>
      <Box sx={{ width: { xs: "82vw", sm: 380 } }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <ErrorOutlineIcon
            sx={{ color: theme.palette.warning.main, mt: 0.25 }}
          />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" style={{ fontWeight: 600 }}>
              {t("delete_custom.title", { label: `${label}${noteSuffix}` })}
            </Typography>
            <Typography
              variant="body2"
              style={{
                marginTop: theme.spacing(1),
                color: theme.palette.text.secondary,
              }}
            >
              {t("delete_custom.body", { label: `${label}${noteSuffix}` })}
            </Typography>
            <Typography
              variant="body2"
              style={{
                marginTop: theme.spacing(1),
                color: theme.palette.text.secondary,
              }}
            >
              {t("delete_custom.detail")}
            </Typography>
          </Box>
          <IconButton onClick={onCancel} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Stack
          direction="row"
          spacing={1.5}
          justifyContent="flex-end"
          sx={{ mt: 3 }}
        >
          <Button variant="outlined" color="primary" onClick={onCancel}>
            {t("delete_custom.cancel")}
          </Button>
          <Button variant="contained" color="error" onClick={onConfirm}>
            {t("delete_custom.confirm")}
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
};

export default DeleteCustomDateDialog;
